import { Icon } from '@clarion/ClarionIcon';
import { RefObject } from 'react';
import {
  composeRenderProps,
  Link as RACLink,
  LinkProps as RACLinkProps,
  TooltipTrigger,
} from 'react-aria-components';
import { cx } from '../util';
import { buttonStyles, ClarionButtonOrLinkProps } from './ClarionButton';
import { ClarionSpinner } from './ClarionSpinner';
import { ClarionTooltip } from './ClarionTooltip';

export interface ClarionButtonLinkProps extends ClarionButtonOrLinkProps, RACLinkProps {
  ref?: RefObject<HTMLAnchorElement>;
}

// Wrapper around RAC Link matching ClarionButton API
export const ClarionButtonLink = ({
  ref,
  icon,
  endIcon,
  children,
  tooltip,
  tooltipPlacement = 'bottom',
  ...props
}: ClarionButtonLinkProps) => {
  // Make icon color somewhat muted compared to button text color (for no intent)
  const iconClassName =
    !props.intent || props.intent === 'none' ? 'text-gray-600 dark:text-gray-300' : undefined;

  const link = (
    <RACLink
      ref={ref}
      // If tooltip is provided, use it as the aria-label (will be overridden by explicit aria-label in the ...props spread below)
      aria-label={tooltip}
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        buttonStyles({ ...renderProps, ...props, className }),
      )}
      {...(props.variant === 'selected' ? { 'aria-current': 'page' } : {})}
    >
      {composeRenderProps(children, (children) => (
        <>
          {props.loading ? (
            <ClarionSpinner size={16} />
          ) : (
            icon && <Icon icon={icon} className={iconClassName} />
          )}
          {children && (
            <span
              className={cx(
                'min-w-0',
                (props.textAlign === 'left' || props.textAlign === 'right') && 'grow',
              )}
            >
              {children}
            </span>
          )}
          {endIcon && <Icon icon={endIcon} className={iconClassName} />}
        </>
      ))}
    </RACLink>
  );

  if (tooltip) {
    return (
      <TooltipTrigger delay={500}>
        {link}
        <ClarionTooltip placement={tooltipPlacement}>{tooltip}</ClarionTooltip>
      </TooltipTrigger>
    );
  }

  return link;
};
